import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { addCommas, digitsEnToFa } from '@persian-tools/persian-tools';
import { firstValueFrom, Observable } from 'rxjs';
import { FileEntity, StockItem } from 'src/app/core';
import {
  ColorDict,
  colorsPair,
  DatabaseIncomeFileBlock,
  DataChip,
  getMainFileOrError,
  Order,
  OrderAction,
  OrderDto,
  OrderStatuses,
} from 'src/app/shared';
import { AreYouSureComponent } from '../../components';
import { BindingsService, FilesService } from '../../services';
import { OrderActionHandlerService } from './order-action-handler.service';

const _ = require('lodash');

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartHandlerService extends EventEmitter {
  private createdOrder!: Order;

  constructor(
    private dialog: MatDialog,
    private filesService: FilesService,
    private bindingService: BindingsService,
    private actionHandler: OrderActionHandlerService
  ) {
    super(true);
  }

  /**
   * Returns persian type
   * @param orderObject The order Object you want to get its type
   * @returns {string} persian types like 'پرینت';
   */
  public getType(orderObject: Order | OrderDto): string {
    const type = orderObject.type;

    if (type === 'print') return 'پرینت';
    if (type === 'thesis') return 'پایان‌نامه';
    if (type === 'thesis-edit') return 'ویرایش پایان‌نامه';

    return 'تعریف نشده';
  }

  /**
   * Handles the action for one specific
   * order that specified with id.
   */
  handleAction<AT extends Order | boolean>(
    action: OrderAction
  ): Observable<AT | Order | boolean> {
    return this.actionHandler.run(action);
  }

  /**
   * Get the w of order as persian
   * @returns {string}
   */
  public getDisplayStatus(orderObject: Order | OrderDto) {
    let order = orderObject;

    if ('fileId' in order) {
      order = order as OrderDto;

      if (order.type === 'thesis-edit') {
        return 'در انتظار محاسبه قیمت';
      } else {
        return 'در انتظار پرداخت';
      }
    } else {
      order = order as Order;

      switch (order.status) {
        case 'calculation':
          return 'در انتظار محاسبه قیمت';

        case 'progress':
          return 'در حال انجام';

        default:
          return 'در انتظار پرداخت';
      }
    }
  }

  /**
   * Get real status of order.
   * @returns {string}
   */
  public getRealStatus(orderObject: Order | OrderDto): OrderStatuses {
    if (orderObject.type === 'thesis-edit') {
      return 'calculation';
    } else {
      return 'payment';
    }
  }

  public removeOrderFromShoppingCart(orderObject: Order | OrderDto) {
    this.dialog
      .open<AreYouSureComponent, unknown, boolean>(AreYouSureComponent)
      .afterClosed()
      .subscribe((data) => {
        const isOk = !!data;
        if (isOk) this.emit(orderObject);
      });
  }

  public getPrice(orderObject: Order | OrderDto): string {
    const order = orderObject as any as Order;
    if (!order) return '0';

    return digitsEnToFa(addCommas(order.price)) + ' تومان';
  }

  // Private methods ---------------------------->

  private getColorChip(order: Order): DataChip[] {
    const blocks = getMainFileOrError(order.files)?.fileBlocks || [];
    return _.uniqBy(
      _.compact(
        blocks.map((block: any) => {
          const newBlock = block as any as DatabaseIncomeFileBlock;
          const color = this.getColor(newBlock);
          const chip = this.constructColorChip(color);

          return chip;
        })
      ),
      'name'
    );
  }

  private getCopiesNumberChip(order: Order): DataChip {
    return { name: 'تعداد نسخه', data: order.copiesNumber };
  }

  private getBindingChip(order: Order): DataChip | undefined {
    const binding = order.binding;
    if (!binding) return binding;
    return { name: 'صحافی', data: binding.name };
  }

  private getPrintSizeChip(order: Order): DataChip | undefined {
    const orderFile = getMainFileOrError(order.files);
    const fileBlocks = orderFile?.fileBlocks!;

    if (fileBlocks?.length !== 1) {
      return undefined;
    }

    const block = fileBlocks[0] as any as DatabaseIncomeFileBlock;
    const size = block.stock.name;

    return { name: 'اندازه چاپ', data: size };
  }

  private generatePrintPageNumber(order: Order): DataChip {
    const data = order.pageCount.toString();
    const name = 'تعداد صفحات';

    return { name, data };
  }

  private getSideChip(order: Order): DataChip | undefined {
    const fileBlocks = this.getFileBlocks(order);
    if (fileBlocks.length === 1) {
      const side = this.getSide(fileBlocks[0]);

      return { name: '', data: side, justData: true };
    }

    return undefined;
  }

  private getFileBlocks(order: Order): DatabaseIncomeFileBlock[] {
    return (
      (getMainFileOrError(order.files)
        .fileBlocks as any[] as DatabaseIncomeFileBlock[]) || []
    );
  }

  private getSide(block: DatabaseIncomeFileBlock): string {
    const type = this.getTypeTrim(block);
    const splitted = _.compact(this.splitType(type));
    const sideNumber = splitted[0];

    if (+sideNumber === 1) return 'یک رو';
    else return 'دو رو';
  }

  private getColor(block: DatabaseIncomeFileBlock) {
    const type = this.getTypeTrim(block);
    const splitted = this.splitType(type);
    const color = splitted[1] as keyof ColorDict;

    return color;
  }

  private constructColorChip(color: keyof ColorDict): DataChip {
    return { data: colorsPair[color], name: 'رنگ' };
  }

  private getTypeTrim(block: DatabaseIncomeFileBlock) {
    return block.stock.type?.trim();
  }

  private getBindingById(id: number): Promise<StockItem> {
    return new Promise<StockItem>((resolve, reject) => {
      this.bindingService.getById(id).subscribe(resolve, reject);
    });
  }

  private async getFileById(id: number): Promise<FileEntity> {
    return firstValueFrom(this.filesService.getOneSingleFile(id));
  }

  private splitType(type: string | undefined) {
    return _.compact(type?.split(/(\d+)/));
  }
}
